/**
 * Paint colour search aliases — starter set (2026-07-05, expanded 2026-07-07,
 * expanded again 2026-07-08 with the top-searched Australian trade colours).
 *
 * Each entry maps a branded paint colour NAME (Dulux / Resene / Taubmans /
 * Wattyl) to a reference HEX approximation. When a customer searches by
 * branded name, ColourMatch finds the nearest colour in its own library by
 * LAB delta-E and surfaces it as the "Closest ColourMatch match" — we
 * explicitly do NOT claim these are official brand colours or endorse the
 * brands. Hex values are publicly-documented approximations sourced from
 * paint-industry references; they are used only for perceptual
 * nearest-neighbour lookup.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  PERMANENT RULE — SINGLE SOURCE OF TRUTH (adopted 2026-07-07)
 * ─────────────────────────────────────────────────────────────────────────
 *  Every alias here is a REFERENCE ONLY. The actual result surfaced to the
 *  user (pinned card, selected swatch, applied colour) MUST always resolve
 *  to a colour that already exists in the ColourMatch library via
 *  `nearestColour()` in `/app/frontend/src/utils/paintSearch.js`.
 *
 *  When growing this file:
 *    • Adding a new trade name is fine — but only as a reference hex.
 *    • Never invent a new "ColourMatch colour name" or a new hex that
 *      isn't in the library.
 *    • Never bypass `searchPalette()` / `nearestColour()` — those two
 *      functions are the only paths through which an alias reaches the UI,
 *      and both are hard-wired to look up the ColourMatch library.
 *    • Aliases point INTO the library; they never extend it.
 * ─────────────────────────────────────────────────────────────────────────
 */
const A = (query, brand, displayName, hex) => ({ query, brand, displayName, hex });

export const PAINT_ALIASES = [
  // ── Dulux ────────────────────────────────────────────────────────
  A("dulux lexicon", "Dulux", "Lexicon", "#EEF2ED"),
  A("dulux lexicon quarter", "Dulux", "Lexicon Quarter", "#EBEBE1"),
  A("dulux lexicon half", "Dulux", "Lexicon Half", "#E9EAE0"),
  A("dulux natural white", "Dulux", "Natural White", "#F0EBD8"),
  A("dulux whisper white", "Dulux", "Whisper White", "#ECECD9"),
  A("dulux hog bristle", "Dulux", "Hog Bristle", "#D2C6A7"),
  A("dulux hog bristle half", "Dulux", "Hog Bristle Half", "#DDD3BC"),
  A("dulux hog bristle quarter", "Dulux", "Hog Bristle Quarter", "#E5DDCB"),
  A("dulux antique white usa", "Dulux", "Antique White USA", "#E9E1CB"),
  A("dulux vivid white", "Dulux", "Vivid White", "#F4F4EF"),
  A("dulux wheat", "Dulux", "Wheat", "#E8DAB8"),
  A("dulux milton moon", "Dulux", "Milton Moon", "#DDDCD0"),
  A("dulux buffed concrete", "Dulux", "Buffed Concrete", "#D9D5C7"),
  A("dulux domino", "Dulux", "Domino", "#3F3F3F"),
  A("dulux monument", "Dulux", "Monument", "#3B3E3D"),
  // 2026-07-08 top-searched additions
  A("dulux white on white", "Dulux", "White on White", "#F3EFE1"),
  A("dulux china white", "Dulux", "China White", "#EDE7D6"),
  A("dulux snowy mountains", "Dulux", "Snowy Mountains", "#EEE8D8"),
  A("dulux snowy mountains half", "Dulux", "Snowy Mountains Half", "#F1EDDE"),
  A("dulux snowy mountains quarter", "Dulux", "Snowy Mountains Quarter", "#F4F0E5"),
  A("dulux white duck", "Dulux", "White Duck", "#DED4C3"),
  A("dulux white duck half", "Dulux", "White Duck Half", "#E9E1D2"),
  A("dulux white duck quarter", "Dulux", "White Duck Quarter", "#EFE9DD"),
  A("dulux grey pail", "Dulux", "Grey Pail", "#DAD3C4"),
  A("dulux grey pail half", "Dulux", "Grey Pail Half", "#E5DFD2"),
  A("dulux grey pebble", "Dulux", "Grey Pebble", "#C9C4B5"),
  A("dulux beige royal", "Dulux", "Beige Royal", "#D9C79E"),
  A("dulux beige royal half", "Dulux", "Beige Royal Half", "#E0D5B3"),
  A("dulux beige royal quarter", "Dulux", "Beige Royal Quarter", "#EAE0C5"),
  A("dulux colorbond surfmist", "Dulux", "Colorbond Surfmist", "#E4DDD1"),
  A("dulux colorbond monument", "Dulux", "Colorbond Monument", "#3B3D3F"),
  A("dulux colorbond woodland grey", "Dulux", "Colorbond Woodland Grey", "#454A47"),
  A("dulux colorbond basalt", "Dulux", "Colorbond Basalt", "#5B5B5C"),
  A("dulux colorbond shale grey", "Dulux", "Colorbond Shale Grey", "#A5A5A5"),
  A("dulux colorbond cove", "Dulux", "Colorbond Cove", "#767A7B"),
  A("dulux tranquil retreat", "Dulux", "Tranquil Retreat", "#D8D2C1"),
  A("dulux self destruct", "Dulux", "Self Destruct", "#B2A99A"),
  A("dulux klavier", "Dulux", "Klavier", "#2E2E2C"),
  A("dulux black", "Dulux", "Black", "#1C1C1C"),

  // ── Resene ───────────────────────────────────────────────────────
  A("resene black white", "Resene", "Black White", "#F2F1EA"),
  A("resene half black white", "Resene", "Half Black White", "#EDECE1"),
  A("resene quarter black white", "Resene", "Quarter Black White", "#E7E5DA"),
  A("resene alabaster", "Resene", "Alabaster", "#F2EFE2"),
  A("resene half alabaster", "Resene", "Half Alabaster", "#E8E5D5"),
  A("resene rice cake", "Resene", "Rice Cake", "#EBE7D8"),
  A("resene sea fog", "Resene", "Sea Fog", "#E4E2D6"),
  A("resene half sea fog", "Resene", "Half Sea Fog", "#DFDCCE"),
  A("resene quarter sea fog", "Resene", "Quarter Sea Fog", "#EBE9DC"),
  A("resene merino", "Resene", "Merino", "#F5EDDD"),
  A("resene half merino", "Resene", "Half Merino", "#ECE4D2"),
  A("resene truffle", "Resene", "Truffle", "#B4A99B"),
  A("resene wheatfield", "Resene", "Wheatfield", "#E6D9BE"),
  A("resene double sea fog", "Resene", "Double Sea Fog", "#D8D5C7"),
  A("resene ironsand", "Resene", "Ironsand", "#544F45"),
  // 2026-07-08 top-searched additions
  A("resene delta", "Resene", "Delta", "#A6A29A"),
  A("resene half delta", "Resene", "Half Delta", "#BEBAB2"),
  A("resene quarter delta", "Resene", "Quarter Delta", "#CDCAC1"),
  A("resene eighth delta", "Resene", "Eighth Delta", "#DAD7CF"),
  A("resene napa", "Resene", "Napa", "#B2A79B"),
  A("resene half napa", "Resene", "Half Napa", "#C4B9AB"),
  A("resene quarter napa", "Resene", "Quarter Napa", "#D6CCC0"),
  A("resene eighth napa", "Resene", "Eighth Napa", "#EAE1D4"),
  A("resene bianca", "Resene", "Bianca", "#F1EAD5"),
  A("resene half bianca", "Resene", "Half Bianca", "#F0EBD8"),
  A("resene quarter bianca", "Resene", "Quarter Bianca", "#EDE7D0"),
  A("resene barely there", "Resene", "Barely There", "#E8E5D3"),
  A("resene wan white", "Resene", "Wan White", "#F0EDDD"),
  A("resene quarter wan white", "Resene", "Quarter Wan White", "#F7F5E9"),
  A("resene half spanish white", "Resene", "Half Spanish White", "#F0E6C7"),
  A("resene spanish white", "Resene", "Spanish White", "#EDDDB0"),
  A("resene villa white", "Resene", "Villa White", "#F3EEDC"),
  A("resene half villa white", "Resene", "Half Villa White", "#EDE6CD"),
  A("resene blanc", "Resene", "Blanc", "#F3EEE0"),
  A("resene half rice cake", "Resene", "Half Rice Cake", "#EFEBDA"),
  A("resene tea", "Resene", "Tea", "#C6B896"),
  A("resene half truffle", "Resene", "Half Truffle", "#C7BFAE"),
  A("resene quarter truffle", "Resene", "Quarter Truffle", "#DAD3C0"),
  A("resene half eighth merino", "Resene", "Half Eighth Merino", "#F1EAD5"),
  A("resene eighth merino", "Resene", "Eighth Merino", "#F5EFDE"),
  A("resene sisal", "Resene", "Sisal", "#D5C8AA"),
  A("resene duck egg blue", "Resene", "Duck Egg Blue", "#9DBCAA"),
  A("resene half duck egg blue", "Resene", "Half Duck Egg Blue", "#B7CFBE"),
  A("resene quarter duck egg blue", "Resene", "Quarter Duck Egg Blue", "#CBDDD0"),
  A("resene karaka", "Resene", "Karaka", "#3A3A2E"),
  A("resene nero", "Resene", "Nero", "#312D2A"),
  A("resene foundry", "Resene", "Foundry", "#403C36"),
  A("resene colorbond surfmist", "Resene", "Colorbond Surfmist", "#E4DDD1"),
  A("resene colorbond monument", "Resene", "Colorbond Monument", "#3B3D3F"),

  // ── Taubmans ─────────────────────────────────────────────────────
  A("taubmans cotton sheets", "Taubmans", "Cotton Sheets", "#F1EFE2"),
  A("taubmans feather soft", "Taubmans", "Feather Soft", "#ECEAD9"),
  A("taubmans dune white", "Taubmans", "Dune White", "#EDE7D3"),
  A("taubmans white trellis", "Taubmans", "White Trellis", "#F0EBD5"),
  A("taubmans whipped butter", "Taubmans", "Whipped Butter", "#F2EBD1"),
  A("taubmans winter cotton", "Taubmans", "Winter Cotton", "#F4F0E4"),
  A("taubmans ivory silk", "Taubmans", "Ivory Silk", "#F1E9CE"),
  A("taubmans muslin", "Taubmans", "Muslin", "#E8DFC3"),
  A("taubmans chalk mount", "Taubmans", "Chalk Mount", "#E7E1CE"),
  A("taubmans silky solstice", "Taubmans", "Silky Solstice", "#EDE4CE"),
  A("taubmans alabaster shell", "Taubmans", "Alabaster Shell", "#F1ECE0"),
  A("taubmans antique cotton", "Taubmans", "Antique Cotton", "#E7DCB8"),
  A("taubmans wild rice", "Taubmans", "Wild Rice", "#DBCEA2"),
  A("taubmans black caviar", "Taubmans", "Black Caviar", "#2B2B2B"),
  A("taubmans indian night", "Taubmans", "Indian Night", "#3E3E3E"),
  // 2026-07-08 top-searched additions
  A("taubmans cotton", "Taubmans", "Cotton", "#EDE9D9"),
  A("taubmans ancient ivory", "Taubmans", "Ancient Ivory", "#EBDFC5"),
  A("taubmans white amber", "Taubmans", "White Amber", "#F1E7CB"),
  A("taubmans pearlwhite", "Taubmans", "Pearlwhite", "#F0EBDC"),
  A("taubmans white shoulder", "Taubmans", "White Shoulder", "#EFE8D3"),
  A("taubmans pale beige", "Taubmans", "Pale Beige", "#DED4B9"),
  A("taubmans green snow", "Taubmans", "Green Snow", "#D6DAC6"),
  A("taubmans wattle bark", "Taubmans", "Wattle Bark", "#5A4A38"),
  A("taubmans stone circle", "Taubmans", "Stone Circle", "#D4CDBB"),
  A("taubmans oyster stone", "Taubmans", "Oyster Stone", "#D9CFBB"),
  A("taubmans natural hessian", "Taubmans", "Natural Hessian", "#D3C6A6"),

  // ── Wattyl ───────────────────────────────────────────────────────
  A("wattyl white pearl", "Wattyl", "White Pearl", "#F0EBDD"),
  A("wattyl chalky white", "Wattyl", "Chalky White", "#F1ECE0"),
  A("wattyl milkbath", "Wattyl", "Milkbath", "#F3EDDC"),
  A("wattyl whipped cream", "Wattyl", "Whipped Cream", "#F4EEDC"),
  A("wattyl ecru", "Wattyl", "Ecru", "#EAE0CB"),
  A("wattyl cameo silk", "Wattyl", "Cameo Silk", "#EDE3CE"),
  A("wattyl pashmina", "Wattyl", "Pashmina", "#D9CDB6"),
  A("wattyl wisp of grey", "Wattyl", "Wisp of Grey", "#D8D4C9"),
  // 2026-07-08 top-searched additions
  A("wattyl white pearl half", "Wattyl", "White Pearl Half", "#F3EEE1"),
  A("wattyl white cotton", "Wattyl", "White Cotton", "#F1EDDF"),
  A("wattyl cotton chalk", "Wattyl", "Cotton Chalk", "#EEE6D3"),
  A("wattyl cream puff", "Wattyl", "Cream Puff", "#F3EAD6"),
  A("wattyl killara", "Wattyl", "Killara", "#8A7C64"),
];

// Fast lookup — normalised query → index (used by paintSearch.js).
const NORM = (s) => s.toLowerCase().replace(/[^a-z0-9 ]+/g, "").replace(/\s+/g, " ").trim();
export const ALIAS_INDEX = PAINT_ALIASES.map((a, i) => ({ i, norm: NORM(a.query) }));
export const normaliseQuery = NORM;
